import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    Typography,
    CircularProgress,
    Tab,
    Tabs,
    styled,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    getActiveRequestBids,
    getArchivedBids,
    getAwardedBids,
    getSubmittedBids,
    getProjectPricingBids,
    getBidStats,
} from "../../services/rfqService";
import RFQDetails from "./components/RFQDetails";
import MobileRFQCards from "./components/MobileRFQCards";
import ManagedByFilter from "./components/ManagedByFilter";
import ScopeTypeFilter from "./components/ScopeTypeFilter";
import BidStats from "./components/BidStats";
import { updateUserData } from "../../utils/CommonFunction";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";

const StyledHeading = styled("h2")({
    color: "#0D0D0E",
    fontFamily: "Inter Tight",
    fontSize: "24px",
    fontWeight: "500",
});

// Tab styling — mirrors the contractor proposals (RFCs) tab look so the
// two pages feel consistent.
const StyledTabs = styled(Tabs)({
    display: "flex",
    gap: "10px",
    padding: 0,
    minHeight: "40px",
    "& .MuiTabs-indicator": {
        background: "none",
    },
    "& .MuiTabs-flexContainer": {
        gap: "10px",
    },
});

const StyledTab = styled(Tab)(({ selected }) => ({
    color: selected ? "#fff !important" : "#949DAA",
    backgroundColor: selected ? "#0d0d0d" : "#fff",
    border: "1px solid #d7d8d9",
    borderRadius: "10px",
    minHeight: "40px !important",
    fontWeight: 500,
    textTransform: "none",
    marginRight: 0,
    padding: "4px 16px !important",
    fontFamily: "Inter Tight",
    fontSize: "14px",
    "&:hover": {
        backgroundColor: selected ? "#0d0d0d" : "#F7F7F7",
        color: !selected ? "#000" : undefined,
    },
}));

const EMPTY_PAGINATION = {
    totalPages: 1,
    totalCount: 0,
    prevEnable: false,
    nextEnable: false,
};

// Tab definitions — each tab maps to one backend `status` query and one
// "mode" prop that the subcomponents read to render the right section.
const RFQ_TABS = [
    { id: "active", label: "Active Requests" },
    // { id: "pricing", label: "Submitted" },
    { id: "awarded", label: "Submitted" },
    { id: "submitted", label: "Proposed to client" },
    { id: "archived", label: "Archived" },
];

// Per-tab count bubble → which bid-stats key it reads.
const TAB_STAT_KEY = {
    active: "awaiting_confirmation",
    awarded: "approved_by_contractor",
    submitted: "proposed_to_client",
    archived: "archived",
};

const toManagedByParam = (selected) =>
    selected?.length > 0 ? selected.join(",") : undefined;

export default function RFQs() {
    const location = useLocation();
    // Allow other screens to deep-link a specific tab via navigation state,
    // e.g. after submitting a bid we land on the "Submitted" tab. The state
    // carries a tab `id` (see RFQ_TABS); fall back to the first tab.
    const initialTabIndex = (() => {
        const requestedId = location.state?.tab;
        if (!requestedId) return 0;
        const idx = RFQ_TABS.findIndex((t) => t.id === requestedId);
        return idx >= 0 ? idx : 0;
    })();
    const [tabIndex, setTabIndex] = useState(initialTabIndex);
    const [managedByFilter, setManagedByFilter] = useState([]);
    // "" = All (no type param sent), otherwise "design" | "execution".
    const [scopeTypeFilter, setScopeTypeFilter] = useState("");

    // Shared bid stats (header summary + per-tab count bubbles).
    const [bidStats, setBidStats] = useState(null);
    const [bidStatsLoading, setBidStatsLoading] = useState(false);

    // --- Active Requests (status=awaiting-contractor-confirmation) ---
    const [activeLoading, setActiveLoading] = useState(false);
    const [activeRequests, setActiveRequests] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [activePerPage] = useState(20);
    const [activePagination, setActivePagination] = useState(EMPTY_PAGINATION);

    // --- Submitted (status=proposal) ---
    const [submittedLoading, setSubmittedLoading] = useState(false);
    const [submitted, setSubmitted] = useState([]);
    const [submittedPage, setSubmittedPage] = useState(1);
    const [submittedPerPage] = useState(20);
    const [submittedPagination, setSubmittedPagination] =
        useState(EMPTY_PAGINATION);

    // --- Submitted (status=awaiting-project-pricing) ---
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricing, setPricing] = useState([]);
    const [pricingPage, setPricingPage] = useState(1);
    const [pricingPerPage] = useState(20);
    const [pricingPagination, setPricingPagination] =
        useState(EMPTY_PAGINATION);

    // --- Awarded (status=approved-by-contractor) ---
    const [awardedLoading, setAwardedLoading] = useState(false);
    const [awarded, setAwarded] = useState([]);
    const [awardedPage, setAwardedPage] = useState(1);
    const [awardedPerPage] = useState(20);
    const [awardedPagination, setAwardedPagination] =
        useState(EMPTY_PAGINATION);

    // --- Archived (status=archived) ---
    const [archivedLoading, setArchivedLoading] = useState(false);
    const [archived, setArchived] = useState([]);
    const [archivedPage, setArchivedPage] = useState(1);
    const [archivedPerPage] = useState(20);
    const [archivedPagination, setArchivedPagination] =
        useState(EMPTY_PAGINATION);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
    const useCardLayout = isMobile || isTablet;

    // Token validation hook
    const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

    const activeTab = RFQ_TABS[tabIndex];
    const managedByParam = toManagedByParam(managedByFilter);
    const scopeTypeParam = scopeTypeFilter || undefined;

    const applyPagination = (response, setter) => {
        setter({
            totalPages: response?.total_pages || 1,
            totalCount: response?.total_count || 0,
            prevEnable: response?.prev_enable || false,
            nextEnable: response?.next_enable || false,
        });
    };

    const fetchActiveRequests = async (page = 1, perPage = 20) => {
        setActiveLoading(true);
        try {
            const response = await getActiveRequestBids(
                page,
                perPage,
                managedByParam,
                scopeTypeParam,
            );
            setActiveRequests(response?.data || []);
            applyPagination(response, setActivePagination);
        } catch (error) {
            console.error("Error fetching active request bids:", error);
            setActiveRequests([]);
        } finally {
            setActiveLoading(false);
        }
    };

    const fetchSubmitted = async (page = 1, perPage = 20) => {
        setSubmittedLoading(true);
        try {
            const response = await getSubmittedBids(
                page,
                perPage,
                managedByParam,
                scopeTypeParam,
            );
            setSubmitted(response?.data || []);
            applyPagination(response, setSubmittedPagination);
        } catch (error) {
            console.error("Error fetching submitted bids:", error);
        } finally {
            setSubmittedLoading(false);
        }
    };

    const fetchPricing = async (page = 1, perPage = 20) => {
        setPricingLoading(true);
        try {
            const response = await getProjectPricingBids(
                page,
                perPage,
                managedByParam,
                scopeTypeParam,
            );
            setPricing(response?.data || []);
            applyPagination(response, setPricingPagination);
        } catch (error) {
            console.error("Error fetching submitted bids:", error);
        } finally {
            setPricingLoading(false);
        }
    };

    const fetchAwarded = async (page = 1, perPage = 20) => {
        setAwardedLoading(true);
        try {
            const response = await getAwardedBids(
                page,
                perPage,
                managedByParam,
                scopeTypeParam,
            );
            setAwarded(response?.data || []);
            applyPagination(response, setAwardedPagination);
        } catch (error) {
            console.error("Error fetching awarded bids:", error);
        } finally {
            setAwardedLoading(false);
        }
    };

    const fetchArchived = async (page = 1, perPage = 20) => {
        setArchivedLoading(true);
        try {
            const response = await getArchivedBids(
                page,
                perPage,
                managedByParam,
                scopeTypeParam,
            );
            setArchived(response?.data || []);
            applyPagination(response, setArchivedPagination);
        } catch (error) {
            console.error("Error fetching archived bids:", error);
        } finally {
            setArchivedLoading(false);
        }
    };

    useEffect(() => {
        updateUserData();
    }, []);

    useEffect(() => {
        let cancelled = false;
        setBidStatsLoading(true);
        getBidStats()
            .then((data) => {
                if (!cancelled) setBidStats(data);
            })
            .finally(() => {
                if (!cancelled) setBidStatsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Tab-driven fetching — fetch the section associated with the active
    // tab whenever the tab changes OR the section's page changes. Each
    // section paginates independently because each tab keeps its own
    // page state.
    useEffect(() => {
        if (activeTab.id === "active") {
            fetchActiveRequests(activePage, activePerPage);
        } else if (activeTab.id === "submitted") {
            fetchSubmitted(submittedPage, submittedPerPage);
        } else if (activeTab.id === "pricing") {
            fetchPricing(pricingPage, pricingPerPage);
        } else if (activeTab.id === "awarded") {
            fetchAwarded(awardedPage, awardedPerPage);
        } else if (activeTab.id === "archived") {
            fetchArchived(archivedPage, archivedPerPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tabIndex,
        activePage,
        submittedPage,
        pricingPage,
        awardedPage,
        archivedPage,
        managedByParam,
        scopeTypeParam,
    ]);

    const resetAllPages = () => {
        setActivePage(1);
        setSubmittedPage(1);
        setPricingPage(1);
        setAwardedPage(1);
        setArchivedPage(1);
    };

    const handleManagedByFilterChange = (next) => {
        setManagedByFilter(next);
        resetAllPages();
    };

    const handleScopeTypeFilterChange = (next) => {
        setScopeTypeFilter(next);
        resetAllPages();
    };

    const handleActivePageChange = (page) => setActivePage(page);
    const handleSubmittedPageChange = (page) => setSubmittedPage(page);
    const handlePricingPageChange = (page) => setPricingPage(page);
    const handleAwardedPageChange = (page) => setAwardedPage(page);
    const handleArchivedPageChange = (page) => setArchivedPage(page);

    // Per-tab loading flag — header spinner uses this to avoid flashing
    // the inner empty-state while the tab's data is still in flight.
    const loadingForTab =
        activeTab.id === "active"
            ? activeLoading
            : activeTab.id === "submitted"
            ? submittedLoading
            : activeTab.id === "pricing"
            ? pricingLoading
            : activeTab.id === "awarded"
            ? awardedLoading
            : archivedLoading;

    const sharedProps = {
        mode: activeTab.id,
        // Active Requests
        activeRequests,
        activeLoading,
        activePagination,
        activePage,
        onActivePageChange: handleActivePageChange,
        // Proposed to client (status=proposed-to-client)
        submitted,
        submittedLoading,
        submittedPagination,
        submittedPage,
        onSubmittedPageChange: handleSubmittedPageChange,
        // Submitted (status=awaiting-project-pricing)
        pricing,
        pricingLoading,
        pricingPagination,
        pricingPage,
        onPricingPageChange: handlePricingPageChange,
        // Awarded (status=approved-by-contractor)
        awarded,
        awardedLoading,
        awardedPagination,
        awardedPage,
        onAwardedPageChange: handleAwardedPageChange,
        // Archived
        archived,
        archivedLoading,
        archivedPagination,
        archivedPage,
        onArchivedPageChange: handleArchivedPageChange,
    };

    const tabsBar = (
        <StyledTabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons={false}
        >
            {RFQ_TABS.map((t, i) => {
                const statKey = TAB_STAT_KEY[t.id];
                const count =
                    statKey && bidStats ? bidStats[statKey] : undefined;
                const selected = tabIndex === i;
                return (
                    <StyledTab
                        key={t.id}
                        selected={selected}
                        label={
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <span>{t.label}</span>
                                {count != null && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: "20px",
                                            height: "20px",
                                            padding: "0 6px",
                                            borderRadius: "999px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            fontFamily: "Inter Tight",
                                            backgroundColor: selected
                                                ? "#FFFFFF"
                                                : "#EEF0F3",
                                            color: selected
                                                ? "#0D0D0E"
                                                : "#6B7280",
                                        }}
                                    >
                                        {count}
                                    </Box>
                                )}
                            </Box>
                        }
                    />
                );
            })}
        </StyledTabs>
    );

    // The two dropdown filters (scope type + managed by).
    const filterControls = (
        <Box
            sx={{
                flexShrink: 0,
                display: "flex",
                gap: 1.5,
                width: useCardLayout ? "100%" : { xs: 320, sm: 440 },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <ScopeTypeFilter
                    value={scopeTypeFilter}
                    onChange={handleScopeTypeFilterChange}
                />
            </Box>
            <Box sx={{ flex: 1.4, minWidth: 0 }}>
                <ManagedByFilter
                    value={managedByFilter}
                    onChange={handleManagedByFilterChange}
                />
            </Box>
        </Box>
    );

    // Desktop: tabs (left) + filters (right) on one row.
    const filtersRow = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                padding: "4px 2vw 16px",
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                {tabsBar}
            </Box>
            {filterControls}
        </Box>
    );

    // Mobile/tablet: filters on one row, tabs on a scrollable row below.
    const mobileFiltersAndTabs = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                padding: "0 0 12px",
            }}
        >
            {filterControls}
            <Box
                sx={{
                    width: "100%",
                    overflowX: "auto",
                    "&::-webkit-scrollbar": { height: 0 },
                    scrollbarWidth: "none",
                }}
            >
                {tabsBar}
            </Box>
        </Box>
    );

    const statsBar = (
        <Box sx={{ padding: useCardLayout ? "0 0 16px" : "0 2vw 16px" }}>
            <BidStats stats={bidStats} loading={bidStatsLoading} />
        </Box>
    );

    return (
        <div
            style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    typography: "body1",
                    backgroundColor: "#fff",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "6px",
                    fontFamily: "Inter Tight",
                }}
            >
                {/* Security Policy Modal */}
                <SecurityPolicyModal
                    open={showSecurityModal}
                    onConfirm={handleSecurityModalClose}
                />

                {useCardLayout ? (
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                            padding: "1rem",
                        }}
                    >
                        {/* Mobile / Tablet Header */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "2vh 2vw 2vh 2vw",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "22px",
                                    fontWeight: "600",
                                    color: "#0D0D0E",
                                    fontFamily: "Inter Tight",
                                    letterSpacing: "0",
                                }}
                            >
                                RFQs
                            </Typography>
                        </Box>

                        {statsBar}

                        {mobileFiltersAndTabs}

                        <MobileRFQCards
                            {...sharedProps}
                            isMobile={isMobile}
                            isTablet={isTablet}
                        />
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "4vh 2vw 2vh 2vw",
                                background:
                                    "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
                            }}
                        >
                            <StyledHeading>RFQs</StyledHeading>
                        </div>

                        {statsBar}

                        {filtersRow}

                        <Box
                            sx={{
                                padding: "0 1.5vw",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }}
                        >
                            {loadingForTab ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 200,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <RFQDetails {...sharedProps} />
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </div>
    );
}
